import React, { useMemo, useState, useLayoutEffect, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Archive,
  AlertTriangle,
  BookMarked,
  CalendarDays,
  Check,
  CheckSquare,
  Church,
  Clock3,
  HeartHandshake,
  Link2,
  NotebookPen,
  Plus,
  Sparkles,
  User,
  Users,
} from "lucide-react";

import { useNotes } from "@/features/notes/hooks";
import { NoteContentPreview } from "@/features/notes/utils/noteRenderer";
import { formatSmartDate } from "@/lib/date";
import { useDistrictGuard } from "../hooks/useDistrictGuard";
import TopBar from "../components/TopBar";
import EmptyState from "../components/shared/EmptyState";
import SearchInput from "../components/SearchInput";
import { InlineIconButton } from "../components/Forms";
import PageWrapper from "../components/layout/PageWrapper";
import { normalizeText } from "../lib/search-rank";
import { useCoordinatorChurchName } from "../hooks/useCoordinatorChurchName";
import { useRegisterKeviaContext } from "../contexts/KeviaContext";

/* ─── Entity metadata ─────────────────────────────────────────────────────── */
const ENTITY_META = {
  person:    { Icon: User,           color: "text-violet-500 dark:text-violet-300", bg: "bg-violet-50/70 dark:bg-violet-900/20",  label: "Persona"     },
  family:    { Icon: Users,          color: "text-slate-500 dark:text-slate-400",   bg: "bg-slate-100/80 dark:bg-slate-700/30",   label: "Hogar"       },
  church:    { Icon: Church,         color: "text-indigo-400 dark:text-indigo-300", bg: "bg-indigo-50/70 dark:bg-indigo-900/20",  label: "Iglesia"     },
  event:     { Icon: CalendarDays,   color: "text-amber-500 dark:text-amber-300",   bg: "bg-amber-50/70 dark:bg-amber-900/20",    label: "Evento"      },
  visit:     { Icon: HeartHandshake, color: "text-rose-400 dark:text-rose-300",     bg: "bg-rose-50/70 dark:bg-rose-900/20",      label: "Visita"      },
  preaching: { Icon: BookMarked,     color: "text-purple-400 dark:text-purple-300", bg: "bg-purple-50/70 dark:bg-purple-900/20",  label: "Predicación" },
};

/* ─── Tabs ────────────────────────────────────────────────────────────────── */
const TABS = [
  { k: "recientes",  label: "Recientes"  },
  { k: "referencia", label: "Referencia" },
  { k: "vinculadas", label: "Vinculadas" },
  { k: "biblioteca", label: "Biblioteca" },
];

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function groupByTime(notes) {
  const now        = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const weekStart  = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 6);

  const groups = { today: [], week: [], older: [] };
  notes.forEach((n) => {
    const d = new Date(n.updated_locally_at || n.updated_at || n.created_at);
    if (d >= todayStart)     groups.today.push(n);
    else if (d >= weekStart) groups.week.push(n);
    else                     groups.older.push(n);
  });

  return [
    { key: "today", label: "Hoy",          Icon: Sparkles,    notes: groups.today },
    { key: "week",  label: "Esta semana",  Icon: CalendarDays, notes: groups.week  },
    { key: "older", label: "Anteriores",   Icon: Archive,     notes: groups.older },
  ].filter((g) => g.notes.length > 0);
}

/* ─── Hero editorial ──────────────────────────────────────────────────────── */
function NotesHero({ district, count, isCoordinator, churchName }) {
  return (
    <div className="px-3 pt-3 sm:px-4 bg-page-bg">
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1e3a5f] via-[#1e40af] to-[#2563eb] p-4 shadow-[0_4px_20px_rgba(30,64,175,0.25)] dark:shadow-none" style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/[0.06] to-transparent pointer-events-none" />
        <div className="absolute inset-0 rounded-[inherit] overflow-hidden pointer-events-none">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/[0.08] blur-2xl" />
          <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-white/[0.06] blur-2xl" />
        </div>

        <div className="relative z-10 flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-[12px] sm:rounded-[14px] bg-white/10 border border-white/15 flex items-center justify-center">
              <NotebookPen size={20} className="text-white" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-[18px] sm:text-[22px] font-semibold tracking-tight leading-tight text-white truncate">
                {isCoordinator ? "Notas" : "Memoria pastoral"}
              </h2>
              <p className="mt-0.5 text-[13px] text-white/60">
                {isCoordinator ? (churchName || district?.name || "") : (district?.name || "Memoria ministerial")}
              </p>
            </div>
          </div>

          {count > 0 && (
            <div className="shrink-0 flex flex-col items-end justify-center pt-1">
              <p className="text-[22px] font-bold leading-none text-white">{count}</p>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/55 mt-1">Notas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Skeletons ───────────────────────────────────────────────────────────── */
function NoteSkeletons() {
  return (
    <div className="space-y-3 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="bg-white dark:bg-slate-800 rounded-[24px] p-4 border border-slate-200/40 dark:border-slate-700/50 shadow-[0_2px_12px_rgba(0,0,0,0.03)]"
        >
          <div className="h-4 w-1/2 bg-slate-200 dark:bg-slate-700 rounded mb-2.5" />
          <div className="h-3 w-5/6 bg-slate-100 dark:bg-slate-700/60 rounded mb-1.5" />
          <div className="h-3 w-3/4 bg-slate-100 dark:bg-slate-700/60 rounded mb-3" />
          <div className="flex gap-1.5">
            <div className="h-5 w-20 bg-slate-100 dark:bg-slate-700/60 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Entity chip ─────────────────────────────────────────────────────────── */
function EntityChip({ linkedType, linkedLabel }) {
  const meta = ENTITY_META[linkedType];
  if (!meta || !linkedLabel) return null;
  const { Icon, color, bg } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${bg} ${color}`}>
      <Icon size={9} strokeWidth={2.5} />
      {linkedLabel}
    </span>
  );
}

/* ─── Group header ────────────────────────────────────────────────────────── */
function GroupHeader({ label, Icon }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      {Icon && <Icon size={10} className="text-slate-400 dark:text-slate-500 shrink-0" strokeWidth={2} />}
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">{label}</p>
      <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
    </div>
  );
}

/* ─── Note card ───────────────────────────────────────────────────────────── */
const NoteCard = React.memo(function NoteCard({ note, onClick }) {
  const dateStr    = formatSmartDate(note.updated_locally_at || note.updated_at || note.created_at);
  const isLinked   = note.linked_type && note.linked_type !== "general";
  const isRef      = note.is_reference || note.pinned;
  const isChecklist = note.note_type === "checklist";

  return (
    <button
      onClick={onClick}
      className={`list-item-lazy w-full text-left bg-white dark:bg-slate-800 rounded-[24px] px-4 py-4 border border-slate-200/30 dark:border-slate-700/40 shadow-[0_1px_8px_rgba(0,0,0,0.025)] transition-all duration-300 ease-out hover:shadow-[0_4px_14px_rgba(0,0,0,0.04)] active:scale-[0.99] active:opacity-80 ${isRef ? "border-l-[2px] border-l-amber-200/70 dark:border-l-amber-700/40" : ""}`}
    >
      {/* Context row: checklist badge + entity + reference */}
      {(isRef || isLinked || isChecklist) && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {isChecklist && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50/80 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-semibold">
              <CheckSquare size={9} strokeWidth={2} />
              Lista de tareas
            </span>
          )}
          {isRef && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50/80 dark:bg-amber-900/20 text-amber-500 dark:text-amber-300 text-[10px] font-semibold">
              <Link2 size={9} strokeWidth={2.5} />
              Referencia
            </span>
          )}
          {isLinked && (
            <EntityChip linkedType={note.linked_type} linkedLabel={note.linked_label} />
          )}
        </div>
      )}

      {/* Title */}
      <p className={`font-semibold leading-snug ${
        note.title?.trim()
          ? "text-[15px] text-slate-900 dark:text-slate-100"
          : "text-[14px] text-slate-400 dark:text-slate-500 italic"
      }`}>
        {note.title?.trim() || "Sin título"}
      </p>

      {/* Content preview: checklist items OR text */}
      {isChecklist ? (
        note.checklist_items?.length > 0 && (
          <div className="mt-1.5 space-y-1 opacity-[0.88]">
            {note.checklist_items.slice(0, 3).map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-1.5 text-[13px] leading-snug ${item.done ? "opacity-50" : ""}`}
              >
                <span className={`h-3.5 w-3.5 rounded-[3px] border shrink-0 flex items-center justify-center ${item.done ? "bg-emerald-500 border-emerald-500" : "border-slate-300 dark:border-slate-500"}`}>
                  {item.done && <Check size={8} className="text-white" strokeWidth={2.5} />}
                </span>
                <span className={`text-slate-500 dark:text-slate-400 truncate ${item.done ? "line-through" : ""}`}>
                  {item.text}
                </span>
              </div>
            ))}
            {note.checklist_items.length > 3 && (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 pl-5">
                +{note.checklist_items.length - 3} más
              </p>
            )}
          </div>
        )
      ) : (
        note.content?.trim() && (
          <div className="mt-1.5 opacity-[0.88]">
            <NoteContentPreview content={note.content} maxLines={2} />
          </div>
        )
      )}

      {/* Footer: tags + date */}
      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        {note.tags?.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100/80 dark:bg-slate-700/60 text-slate-400 dark:text-slate-500 text-[10px] font-medium"
          >
            {tag}
          </span>
        ))}
        {note.tags?.length > 3 && (
          <span className="text-[10px] text-slate-400 dark:text-slate-500">+{note.tags.length - 3}</span>
        )}
        <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto shrink-0 flex items-center gap-0.5">
          <Clock3 size={9} />
          {dateStr}
        </span>
      </div>
    </button>
  );
});

/* ─── Empty state ─────────────────────────────────────────────────────────── */
function EmptyNotes({ filter, onNew }) {
  const msgs = {
    recientes:  { icon: NotebookPen, label: "Sin notas",              hint: "Registra observaciones pastorales, contexto de personas o reflexiones ministeriales." },
    referencia: { icon: Link2,       label: "Sin notas de referencia", hint: "Marca notas importantes como Referencia para accederlas rápido."                      },
    vinculadas: { icon: Users,       label: "Sin notas vinculadas",    hint: "Las notas ligadas a personas, hogares o eventos aparecerán aquí."                     },
    biblioteca: { icon: BookMarked,  label: "Biblioteca vacía",        hint: "Archiva notas para mantener el historial pastoral organizado."                        },
  };
  const { icon: Icon, label, hint } = msgs[filter] || msgs.recientes;

  return (
    <div className="animate-fade flex flex-col items-center justify-center pt-14 px-8 text-center">
      <div className="h-14 w-14 bg-slate-100/80 dark:bg-slate-700/50 rounded-[20px] flex items-center justify-center mb-4">
        <Icon size={26} className="text-violet-300 dark:text-violet-500" strokeWidth={1.75} />
      </div>
      <p className="text-[15px] font-semibold text-slate-700 dark:text-slate-300 mb-1">{label}</p>
      <p className="text-[13px] text-slate-400 dark:text-slate-500 leading-relaxed max-w-[230px]">{hint}</p>
      {filter === "recientes" && (
        <button
          onClick={onNew}
          className="mt-5 inline-flex items-center gap-2 bg-violet-600 text-white rounded-[20px] px-6 py-3 font-semibold text-[14px] shadow-[0_4px_12px_rgba(109,40,217,0.25)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
        >
          <Plus size={15} />
          Nueva nota
        </button>
      )}
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────────────────────── */
export default function Notes() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { district, coordinatorChurchId } = useDistrictGuard();
  const coordinatorChurchName = useCoordinatorChurchName();

  const urlFilter = searchParams.get("filter");
  const linkedType  = searchParams.get("linked_type")  || null;
  const linkedId    = searchParams.get("linked_id")    || null;
  const linkedLabel = searchParams.get("linked_label") || null;

  // Persist tab choice in session; skip when entity-scoped (linked notes have no persistent context)
  const [filter, setFilter] = useState(
    urlFilter || (!linkedType && sessionStorage.getItem("keves:notes:filter")) || "recientes"
  );
  const [search,  setSearch]  = useState("");

  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem("keves:notes:scrollY");
      if (saved) { window.scrollTo(0, parseInt(saved, 10)); sessionStorage.removeItem("keves:notes:scrollY"); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    return () => { try { sessionStorage.setItem("keves:notes:scrollY", String(Math.round(window.scrollY))); } catch { /* ignore */ } };
  }, []);

  const { notes, loading, error, refetch } = useNotes({ filter, linkedType, linkedId });

  const filtered = useMemo(() => {
    if (!search.trim()) return notes;
    const q = normalizeText(search);
    return notes.filter(
      (n) =>
        normalizeText(n.title).includes(q) ||
        normalizeText(n.content).includes(q) ||
        n.tags?.some((t) => normalizeText(t).includes(q)) ||
        normalizeText(n.linked_label).includes(q)
    );
  }, [notes, search]);

  const grouped = useMemo(
    () => (search.trim() ? null : groupByTime(filtered)),
    [filtered, search]
  );

  // Kevia dynamic context — registered while this page is mounted
  const keviaCtx = useMemo(() => {
    if (loading) return null;
    const filterLabels = {
      recientes:  "Recientes",
      referencia: "Referencia",
      vinculadas: "Vinculadas",
      biblioteca: "Biblioteca",
    };
    const lines = [
      "Módulo: Notas pastorales",
      `  Filtro activo: ${filterLabels[filter] ?? filter}`,
      `  Notas en vista: ${notes.length}`,
    ];
    return {
      type:         "module",
      id:           `notes-${filter}`,
      name:         "Notas",
      tone:         "neutral",
      contextBlock: `[Contexto activo en Kéves]\n${lines.join("\n")}\n\n[Pregunta del pastor]\n`,
    };
  }, [loading, filter, notes.length]);
  useRegisterKeviaContext(keviaCtx);

  const handleNew  = () => nav("/notes/new");
  const handleOpen = (id) => nav(`/notes/${id}`);

  return (
    <PageWrapper onRefresh={refetch}>

      <TopBar
        title="Notas"
        subtitle={coordinatorChurchId ? (coordinatorChurchName || undefined) : (linkedLabel || "Memoria pastoral")}
        onBack={false}
        right={
          <InlineIconButton
            icon={Plus}
            onClick={handleNew}
            accent="navy"
            aria-label="Nueva nota"
          />
        }
      />

      {/* SEARCH */}
      <div className="px-4 pt-2 pb-1">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar nota, persona, etiqueta..."
        />
      </div>

      {/* HERO — siempre visible en vista general (no entity-scoped) */}
      {!linkedType && (
        <NotesHero
          district={district}
          count={notes.length}
          isCoordinator={!!coordinatorChurchId}
          churchName={coordinatorChurchName}
        />
      )}

      {/* TABS */}
      <div className="px-4 pt-3">
        <div className="flex gap-1 bg-slate-200/50 dark:bg-slate-800/60 supports-[backdrop-filter]:bg-slate-200/30 dark:supports-[backdrop-filter]:bg-slate-800/40 backdrop-blur-xl rounded-full p-1 border border-slate-200/30 dark:border-slate-700/40">
          {TABS.map((tab) => (
            <button
              key={tab.k}
              onClick={() => {
                setFilter(tab.k);
                setSearch("");
                if (!linkedType) sessionStorage.setItem("keves:notes:filter", tab.k);
                if (linkedId) nav("/notes?filter=" + tab.k, { replace: true });
              }}
              className={`flex-1 h-11 flex items-center justify-center rounded-[20px] text-[11px] font-semibold transition-all duration-200 ease-out active:scale-[0.98] ${
                filter === tab.k
                  ? "bg-white/90 dark:bg-slate-700/90 text-violet-600 dark:text-violet-300 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* LIST */}
      <div className="px-4 pt-3 pb-5">

        {loading && <NoteSkeletons />}

        {/* ERROR */}
        {!loading && error && (
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            actionOnClick={refetch}
          />
        )}

        {/* Empty — sin notas en este filtro */}
        {!loading && !error && filtered.length === 0 && !search.trim() && (
          <EmptyNotes filter={filter} onNew={handleNew} />
        )}

        {/* Empty — sin resultados de búsqueda */}
        {!loading && filtered.length === 0 && search.trim() && (
          <div className="animate-fade flex flex-col items-center justify-center pt-12 px-6 text-center">
            <p className="text-[15px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Sin resultados</p>
            <p className="text-[13px] text-slate-400 dark:text-slate-500 mb-4">
              Nada coincide con &ldquo;{search}&rdquo;
            </p>
            <button
              onClick={() => setSearch("")}
              className="text-[13px] font-semibold text-violet-500 dark:text-violet-400 active:opacity-70"
            >
              Limpiar búsqueda
            </button>
          </div>
        )}

        {/* Búsqueda activa — lista plana sin agrupación */}
        {!loading && search.trim() && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((note) => (
              <NoteCard key={note.id} note={note} onClick={() => handleOpen(note.id)} />
            ))}
          </div>
        )}

        {/* Vista normal — agrupada por tiempo */}
        {!loading && !search.trim() && grouped && (
          <div className="space-y-2">
            {grouped.map((group) => (
              <div key={group.key}>
                <GroupHeader label={group.label} Icon={group.Icon} />
                <div className="space-y-3 pt-1">
                  {group.notes.map((note) => (
                    <NoteCard key={note.id} note={note} onClick={() => handleOpen(note.id)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

    </PageWrapper>
  );
}
